<template>
  <div>

    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">My Leave Applications</h5>
        <p class="page-sub">View your leave history and apply for time off</p>
      </div>
      <button class="btn-primary-action" @click="openApplyModal">
        <font-awesome-icon icon="plus" /> Apply for Leave
      </button>
    </div>

    <!-- Summary cards -->
    <div v-if="balances.length" class="balance-grid">
      <div v-for="b in balances" :key="b.leave_type_id" class="balance-card">
        <div class="balance-label">{{ b.leave_type?.name }}</div>
        <div class="balance-numbers">
          <span class="balance-available">{{ b.available ?? b.entitled }}</span>
          <span class="balance-total"> / {{ b.entitled }} days</span>
        </div>
        <div class="balance-meta">
          <span>Taken: {{ b.taken }}</span>
          <span v-if="b.pending"> · Pending: {{ b.pending }}</span>
        </div>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div class="sk-row-head"></div>
      <div v-for="n in 4" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!leaves.length" class="empty-box">
      <font-awesome-icon icon="calendar-times" class="empty-icon" />
      <p class="empty-title">No leave applications yet</p>
      <p class="empty-sub">Click "Apply for Leave" to submit your first request.</p>
    </div>

    <!-- Table -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>Leave Type</th>
            <th>From</th>
            <th>To</th>
            <th class="text-center">Days</th>
            <th>Reason</th>
            <th>Status</th>
            <th>Applied On</th>
            <th class="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="leave in leaves" :key="leave.id">
            <td class="fw-600">{{ leave.leave_type?.name }}</td>
            <td>{{ fmtDate(leave.from_date) }}</td>
            <td>{{ fmtDate(leave.to_date) }}</td>
            <td class="text-center">{{ leave.days }}</td>
            <td class="text-muted">{{ leave.reason || '—' }}</td>
            <td><span class="chip" :class="statusClass(leave.status)">{{ leave.status }}</span></td>
            <td class="text-muted">{{ fmtDate(leave.created_at) }}</td>
            <td class="text-center">
              <button
                v-if="leave.status === 'pending'"
                class="cancel-btn"
                @click="confirmCancel(leave)"
              >Cancel</button>
              <span v-else class="text-muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination" v-if="meta.last_page > 1">
        <button class="page-btn" :disabled="meta.current_page === 1" @click="goPage(meta.current_page - 1)">‹</button>
        <span class="page-info">{{ meta.current_page }} / {{ meta.last_page }}</span>
        <button class="page-btn" :disabled="meta.current_page === meta.last_page" @click="goPage(meta.current_page + 1)">›</button>
      </div>
    </div>

    <!-- Apply Modal -->
    <div v-if="showApplyModal" class="modal-overlay" @click.self="closeApplyModal">
      <div class="modal-box">
        <div class="modal-header">
          <h6 class="modal-title">Apply for Leave</h6>
          <button class="modal-close" @click="closeApplyModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Leave Type <span class="req">*</span></label>
            <select v-model="form.leave_type_id" class="form-control">
              <option value="">— Select —</option>
              <option v-for="lt in leaveTypes" :key="lt.id" :value="lt.id">{{ lt.name }}</option>
            </select>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">From Date <span class="req">*</span></label>
              <input type="date" v-model="form.from_date" class="form-control" />
            </div>
            <div class="form-group">
              <label class="form-label">To Date <span class="req">*</span></label>
              <input type="date" v-model="form.to_date" class="form-control" :min="form.from_date" />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Reason</label>
            <textarea v-model="form.reason" class="form-control" rows="3" placeholder="Optional reason..."></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Attachment</label>
            <input type="file" @change="handleFile" class="form-control" accept=".jpg,.jpeg,.png,.pdf" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeApplyModal">Cancel</button>
          <button class="btn-submit" :disabled="submitting" @click="submitLeave">
            <span v-if="submitting"><font-awesome-icon icon="spinner" spin /> Submitting...</span>
            <span v-else>Submit Request</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../../lib/reusableMixin';
import moment from 'moment-timezone';

export default {
  name: 'MyLeaves',
  mixins: [reusableMixin],

  data() {
    return {
      leaves: [],
      balances: [],
      leaveTypes: [],
      meta: { current_page: 1, last_page: 1 },
      loading: false,
      showApplyModal: false,
      submitting: false,
      cancellingId: null,
      form: { leave_type_id: '', from_date: '', to_date: '', reason: '', attachment: null },
      toast: useToast(),
    };
  },

  mounted() {
    this.fetchLeaves();
    this.fetchBalances();
    this.fetchLeaveTypes();
  },

  methods: {
    async fetchLeaves(page = 1) {
      this.loading = true;
      try {
        const res = await axios.get(`/hr/self-service/leaves?page=${page}`);
        this.leaves = res.data.data?.data ?? res.data.data ?? [];
        this.meta   = res.data.data?.meta ?? {
          current_page: res.data.data?.current_page ?? 1,
          last_page:    res.data.data?.last_page    ?? 1,
        };
      } catch {
        this.toast.error('Failed to load leave applications.');
      } finally {
        this.loading = false;
      }
    },

    async fetchBalances() {
      try {
        // Fetch the employee's own balances via the self-service leaves endpoint with balance query
        const profile = await axios.get('/hr/self-service/profile');
        const empId   = profile.data.data?.id;
        if (!empId) return;
        const res = await axios.get('/hr/leave/balances', { params: { employee_id: empId } });
        this.balances = res.data.data ?? [];
      } catch { /* silently ignore */ }
    },

    async fetchLeaveTypes() {
      try {
        const res = await axios.get('/hr/self-service/leave-types');
        this.leaveTypes = (res.data.data?.data ?? res.data.data ?? []).filter(lt => lt.is_active);
      } catch { /* silently ignore */ }
    },

    openApplyModal() {
      this.form = { leave_type_id: '', from_date: '', to_date: '', reason: '', attachment: null };
      this.showApplyModal = true;
    },

    closeApplyModal() {
      this.showApplyModal = false;
    },

    handleFile(e) {
      this.form.attachment = e.target.files[0] ?? null;
    },

    async submitLeave() {
      if (!this.form.leave_type_id || !this.form.from_date || !this.form.to_date) {
        this.toast.error('Please fill in all required fields.');
        return;
      }
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append('leave_type_id', this.form.leave_type_id);
        fd.append('from_date',     this.form.from_date);
        fd.append('to_date',       this.form.to_date);
        if (this.form.reason)     fd.append('reason',     this.form.reason);
        if (this.form.attachment) fd.append('attachment', this.form.attachment);

        const res = await axios.post('/hr/self-service/leaves', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.toast.success(res.data.message);
        this.closeApplyModal();
        this.fetchLeaves();
        this.fetchBalances();
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to submit leave request.');
      } finally {
        this.submitting = false;
      }
    },

    async confirmCancel(leave) {
      if (!confirm(`Cancel your leave request from ${this.fmtDate(leave.from_date)} to ${this.fmtDate(leave.to_date)}?`)) return;
      try {
        const res = await axios.put(`/hr/self-service/leaves/${leave.id}/cancel`);
        this.toast.success(res.data.message);
        this.fetchLeaves();
        this.fetchBalances();
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to cancel leave.');
      }
    },

    goPage(page) { this.fetchLeaves(page); },

    fmtDate(d)  { return d ? moment(d).format('DD MMM YYYY') : '—'; },

    statusClass(s) {
      return {
        pending:   'chip--pending',
        approved:  'chip--approved',
        rejected:  'chip--rejected',
        cancelled: 'chip--cancelled',
      }[s] ?? '';
    },
  },
};
</script>

<style scoped>
/* ── Header ── */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }

/* ── Balance cards ── */
.balance-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:14px; margin-bottom:20px; }
.balance-card { background:#fff; border:1px solid #e4eaf2; border-radius:10px; padding:14px 16px; }
.balance-label { font-size:12px; font-weight:600; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; margin-bottom:6px; }
.balance-numbers { display:flex; align-items:baseline; gap:2px; }
.balance-available { font-size:28px; font-weight:700; color:#004AAD; }
.balance-total { font-size:13px; color:#7a90b0; }
.balance-meta { font-size:12px; color:#7a90b0; margin-top:4px; display:flex; gap:8px; }

/* ── Skeleton ── */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }
.sk-row-head { height:44px; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.sk-row { height:52px; border-bottom:1px solid #f0f3f8; background: linear-gradient(90deg,#f4f7fb 25%,#eaeff7 50%,#f4f7fb 75%); background-size:400% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:100% 0} 100%{background-position:-100% 0} }

/* ── Empty ── */
.empty-box { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 20px; text-align:center; background:#fff; border:1px solid #e4eaf2; border-radius:10px; }
.empty-icon { font-size:40px; color:#c8d5e8; margin-bottom:14px; }
.empty-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 5px; }
.empty-sub { font-size:13px; color:#7a90b0; margin:0; }

/* ── Table ── */
.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }
.data-table { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; color:#1a2b45; }
.fw-600 { font-weight:600; }
.text-center { text-align:center; }
.text-muted { color:#7a90b0; }

/* ── Status chips ── */
.chip { display:inline-flex; align-items:center; border-radius:6px; padding:3px 10px; font-size:11.5px; font-weight:600; text-transform:capitalize; border:1px solid transparent; }
.chip--pending   { background:#fefce8; color:#a16207; border-color:#fde68a; }
.chip--approved  { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.chip--rejected  { background:#fff5f5; color:#e03535; border-color:#fecaca; }
.chip--cancelled { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }

/* ── Cancel button ── */
.cancel-btn { font-size:12px; font-weight:600; color:#e03535; background:#fff5f5; border:1px solid #fecaca; border-radius:6px; padding:4px 10px; cursor:pointer; transition:background .12s; }
.cancel-btn:hover { background:#fee2e2; }

/* ── Pagination ── */
.pagination { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #e4eaf2; }
.page-btn { width:30px; height:30px; border:1px solid #dde5f0; border-radius:6px; background:#fff; color:#1a2b45; cursor:pointer; font-size:16px; display:flex; align-items:center; justify-content:center; transition:background .12s; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-btn:not(:disabled):hover { background:#f4f7fb; }
.page-info { font-size:13px; color:#7a90b0; }

/* ── Modal ── */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:520px; box-shadow:0 20px 60px rgba(0,0,0,.15); display:flex; flex-direction:column; max-height:90vh; overflow:hidden; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 20px; border-bottom:1px solid #e4eaf2; }
.modal-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-close:hover { color:#1a2b45; }
.modal-body { padding:20px; overflow-y:auto; display:flex; flex-direction:column; gap:14px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 20px; border-top:1px solid #e4eaf2; }

.form-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-group { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#1a2b45; }
.req { color:#e03535; }
.form-control { border:1px solid #dde5f0; border-radius:8px; padding:9px 12px; font-size:13px; color:#1a2b45; outline:none; width:100%; transition:border .15s; background:#fff; }
.form-control:focus { border-color:#004AAD; }

.btn-cancel { background:#f4f7fb; border:1px solid #dde5f0; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-cancel:hover { background:#e8eef7; }
.btn-submit { background:#004AAD; color:#fff; border:none; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-submit:hover:not(:disabled) { background:#003b8e; }
.btn-submit:disabled { opacity:.6; cursor:not-allowed; }
</style>
